import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "@/assets/logo.png";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

interface NavItem {
  label: string;
  href?: string;
  children?: { label: string; href: string }[];
}

const navItems: NavItem[] = [
  
  { label: "O mně", href: "/o-mne" },
  {
    label: "Nemovitosti",
    children: [
      { label: "Aktuální nabídka", href: "/?sold=false#nabidka" },
      { label: "Zrealizované zakázky", href: "/?sold=true#nabidka" },
    ],
  },
  {
    label: "Služby",
    children: [
      { label: "Prodej nemovitostí", href: "/sluzby/prodej" },
      { label: "Tržní odhad", href: "/sluzby/odhad" },
      { label: "Dědictví – odhad", href: "/sluzby/dedictvi" },
      { label: "Aukce", href: "/sluzby/aukce" },
    ],
  },
  { label: "Reference", href: "/reference" },
  { label: "Kontakty", href: "/kontakt" },
];

const isActive = (item: NavItem, pathname: string) => {
  if (item.href) return pathname === item.href || (item.href !== "/" && pathname.startsWith(item.href));
  if (item.children) return item.children.some(c => {
    const p = c.href.split("#")[0] || "/";
    return pathname === p || (p !== "/" && pathname.startsWith(p));
  });
  return false;
};

const NavLinkItem = ({
  href,
  onClick,
  children,
  className,
}: {
  href: string;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onClick?.();

    // Handle hash links like /#nabidka
    if (href.includes("#")) {
      const [path, hash] = href.split("#");
      const targetPath = path || "/";
      if (location.pathname === targetPath) {
        const el = document.getElementById(hash);
        el?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate(targetPath + "#" + hash);
      }
    } else {
      navigate(href);
    }
  };

  return (
    <a href={href} onClick={handleClick} className={className}>
      {children}
    </a>
  );
};

const DropdownMenu = ({ items, onClose }: { items: { label: string; href: string }[]; onClose: () => void }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 8 }}
    transition={{ duration: 0.2 }}
    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-52 bg-card/95 backdrop-blur-xl border border-border/50 rounded-xl shadow-lg overflow-hidden z-50"
  >
    {items.map((item) => (
      <NavLinkItem
        key={item.label}
        href={item.href}
        onClick={onClose}
        className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
      >
        {item.label}
      </NavLinkItem>
    ))}
  </motion.div>
);

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0); // 0 = top, 1 = fully shrunk
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout>>();
  const { pathname } = useLocation();

  const handleMouseEnter = (label: string) => {
    clearTimeout(dropdownTimeout.current);
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    dropdownTimeout.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  useEffect(() => () => clearTimeout(dropdownTimeout.current), []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileExpanded(null);
  }, [pathname]);

  // Smooth scroll-based interpolation (rAF-throttled to avoid forced reflows)
  useEffect(() => {
    let rafId: number | null = null;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      rafId = requestAnimationFrame(() => {
        const progress = Math.min(1, window.scrollY / 150);
        setScrollProgress(prev => (prev === progress ? prev : progress));
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // Interpolated values: logo 140px → 70px, navbar h-28 (112px) → h-16 (64px)
  const logoHeight = 140 - scrollProgress * 70; // 140 → 70
  const navHeight = 112 - scrollProgress * 48; // 112 → 64

  return (
    <nav className="fixed top-9 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
      <div className="container-luxury flex items-center justify-between px-4 sm:px-6 lg:px-8" style={{ height: `${navHeight}px`, transition: 'none' }}>
        <Link to="/" className="flex items-center">
          <img src={logo} alt="František Babčický" style={{ height: `${logoHeight}px`, transition: 'none' }} />
        </Link>

        {/* Desktop */}
        <div className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => {
            const active = isActive(item, pathname);
            if (item.children) {
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`nav-link-glass flex items-center gap-1 text-sm transition-all font-semibold px-3 py-1.5 rounded-lg relative overflow-hidden ${
                      active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <AnimatePresence>
                    {openDropdown === item.label && (
                      <DropdownMenu items={item.children} onClose={() => setOpenDropdown(null)} />
                    )}
                  </AnimatePresence>
                </div>
              );
            }
            if (item.href) {
              return (
                <NavLinkItem
                  key={item.label}
                  href={item.href}
                  className={`nav-link-glass text-sm transition-all font-semibold px-3 py-1.5 rounded-lg relative overflow-hidden ${
                    active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.label}
                </NavLinkItem>
              );
            }
            return (
              <span
                key={item.label}
                className="nav-link-glass text-sm transition-all font-semibold px-3 py-1.5 rounded-lg relative overflow-hidden text-muted-foreground cursor-default"
              >
                {item.label}
              </span>
            );
          })}
          <Button size="sm" className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90" asChild>
            <Link to="/sluzby/odhad">Bezplatný odhad</Link>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-foreground" aria-label="Menu">
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background border-b border-border overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {navItems.map((item) => {
                const active = isActive(item, pathname);
                return item.children ? (
                  <div key={item.label}>
                    <button
                      onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                      className={`flex items-center justify-between w-full text-sm py-2 font-medium ${
                        active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {item.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${mobileExpanded === item.label ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {mobileExpanded === item.label && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-4 space-y-1 pb-2">
                            {item.children.map((child) => (
                              <NavLinkItem
                                key={child.label}
                                href={child.href}
                                onClick={() => setMobileOpen(false)}
                                className="block text-sm text-muted-foreground hover:text-foreground py-1.5"
                              >
                                {child.label}
                              </NavLinkItem>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : item.href ? (
                  <NavLinkItem
                    key={item.label}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`block text-sm py-2 font-medium ${
                      active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {item.label}
                  </NavLinkItem>
                ) : (
                  <span
                    key={item.label}
                    className="block text-sm py-2 font-medium text-muted-foreground cursor-default"
                  >
                    {item.label}
                  </span>
                );
              })}
              <Button size="sm" className="w-full rounded-xl bg-primary text-primary-foreground mt-2" asChild>
                <Link to="/sluzby/odhad" onClick={() => setMobileOpen(false)}>Bezplatný odhad</Link>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
